import { useContext, useEffect, useState } from "react";

import { useCardController, useDialog } from "@datanac/datanac-mui-toolkit";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Box, FormControl, Grid, IconButton, InputAdornment, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { FinancialIcon } from "components/Icons/Icons";
import { MODULE_FINANCIAL } from "components/Menu/config/constants";
import usePrompt from "components/Utility/usePrompt";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { ValueLabel } from "mui-toolkit/ValueLabel";
import { useSnackbar } from "notistack";
import { formatPrice } from "utility/formatters";
import { v4 as uuidv4 } from "uuid";

import { useChatAssistantAttachments } from "../../../contexts/useChatAssistantAttachments";
import { DeleteIcon } from "../../Icons/Icons";
import useFinanceReport from "../useFinanceReport";
import { BALANCE_SHEET_REQUIRED_FIELDS } from "./BalanceSheetHelper";

export default function BalanceSheetWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const [currentYear, setCurrentYear] = useState(globalState.crop_year);

  const { state: lineItems, actions: lineItemActions } = useCardController();
  const prompt = usePrompt();
  const { addAttachments } = useChatAssistantAttachments();
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const {
    isLoading,

    isLoadingBalanceSheet,
    balanceSheet,
    loadBalanceSheet,
  } = useFinanceReport({
    onError: handleError,
    currentYear,

    loadModules: [MODULE_FINANCIAL],
  });

  useEffect(() => {
    if (balanceSheet) {
      lineItemActions.replace(balanceSheet?.values);
    }
    addAttachments([{ name: "Balance Sheet", content: balanceSheet }]);
  }, [balanceSheet]);

  // --- --- ---

  const handleChangeState_LineItem = async (event, _currentDocumentDictionary) => {
    if (event?.target?.value !== lineItems.find((v) => v.id === _currentDocumentDictionary?.id)?.value) {
      formStatusActions.setIsFormDirty(true, true);

      const _newValue = {
        ..._currentDocumentDictionary,
        id: _currentDocumentDictionary?.id || uuidv4(),
        value: event?.target?.value || 0,
      };

      const _uploadFormData = {
        producer_token: globalState.producer_token,
        agent_token: globalState.agent_token,
        Document: {
          id: balanceSheet?.id || uuidv4(),
          values: [_newValue],
        },
      };

      await UsersApiHelper.updateFinancialDocument(balanceSheet, globalState);

      UsersApiHelper.updateDocumentDictionary(_uploadFormData, globalState)
        .then(() => {
          formStatusActions.setIsSaving(false);
        })
        .catch((err) => {
          formStatusActions.setIsFormDirty(true, false);
          handleError(err);
        })
        .finally(() => {
          loadBalanceSheet();
        });
    }
  };

  const formatName = (value) => {
    if (value) {
      return value.replaceAll("_", " ");
    }
  };

  // --- --- ---

  const handleClickAddNewLineItem = async (category) => {
    prompt.actions.prompt({
      title: "New Item",
      prompt: "Please enter item name:",
      callback: (inputValue) => {
        if (inputValue) {
          const foundItem = lineItems.find(
            (item) => item.item === formatString(inputValue) && item.container === category
          );

          if (foundItem) {
            enqueueSnackbar("Item already exists.", { preventDuplicate: true, variant: "warning" });
            return;
          } else {
            var newLineItem = createLineItem(category, inputValue, 0.0, false /*doOverwrite*/);
            if (newLineItem) {
              handleChangeState_LineItem({ target: { value: 0 } }, newLineItem);
            }
          }
        }
      },
    });
  };

  const createLineItem = (category, name, doOverwrite) => {
    const _id = doOverwrite && lineItems.find((li) => li?.name === name)?.id;

    const formattedName = formatString(name);

    if (formattedName !== undefined && formattedName !== null && formattedName !== "") {
      const _newLineItem = {
        section: null,
        module: "financial",
        object: "balance_sheet",
        container: category,
        item: formattedName,
        id: _id || uuidv4(),
        document_id: balanceSheet?.id,
        value: 0,
        is_active: true,
        new_field: true,
      };

      return _newLineItem;
    } else {
      return;
    }
  };

  const handleDeleteLineItem = async (currentLineItem) => {
    const _newLineItem = { ...currentLineItem };
    _newLineItem.is_active = false;
    lineItemActions.update(_newLineItem);

    formStatusActions.setIsFormDirty(true, true);

    handleChangeState_LineItem({}, _newLineItem);
  };

  const formatString = (name) => {
    if (/[^a-zA-Z0-9\s]/.test(name)) {
      handleError({ message: "Only letters, numbers, and spaces are allowed." });
      return;
    }

    return name.toLowerCase().replace(/\s+/g, "_").replace(/_+/g, "_");
  };

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Balance Sheet"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader
        title="Year"
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        isLoading={isLoading}
        additionalControls={formStatusComponent}
      />

      <Grid container columnSpacing={6} rowSpacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Assets
          </Typography>

          {balanceSheet?.assets_container?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, mt: "58px", pb: "28px", textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(lineItems?.length) &&
                  lineItems
                    ?.filter((v) => v.container === container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            endAdornment={
                              !BALANCE_SHEET_REQUIRED_FIELDS.includes(v.item) && (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => handleDeleteLineItem(v)} edge="end" color="error">
                                    <DeleteIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>
                      </Grid>
                    ))}

                <Grid
                  item
                  xs={6}
                  sx={{ mb: "20px", pt: "44px !important" }}
                  className="navigation"
                  display={"flex"}
                  alignItems={"end"}
                >
                  <Button
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleClickAddNewLineItem(container)}
                  >
                    <AddCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6">Totals</Typography>
          <Box className="flexWrapContainer">
            <ValueLabel
              label="Total Assets"
              value={formatPrice(balanceSheet?.assets?.total, 0)}
              isLoading={isLoadingBalanceSheet}
            />
            <ValueLabel
              label="Current Assets"
              value={formatPrice(balanceSheet?.assets_current?.total, 0)}
              isLoading={isLoadingBalanceSheet}
            />
            <ValueLabel
              label="Long-term Assets"
              value={formatPrice(balanceSheet?.assets_noncurrent?.total, 0)}
              isLoading={isLoadingBalanceSheet}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6} className="pageBreakBefore">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Liabilities
          </Typography>

          {balanceSheet?.liabilities_container?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, mt: "58px", pb: "28px", textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(lineItems?.length) &&
                  lineItems
                    ?.filter((v) => v.container === container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            endAdornment={
                              !BALANCE_SHEET_REQUIRED_FIELDS.includes(v.item) && (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => handleDeleteLineItem(v)} edge="end" color="error">
                                    <DeleteIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}

                <Grid
                  item
                  xs={6}
                  sx={{ mb: "20px", pt: "44px !important" }}
                  className="navigation"
                  display={"flex"}
                  alignItems={"end"}
                >
                  <Button
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleClickAddNewLineItem(container)}
                  >
                    <AddCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6">Totals</Typography>
          <Box className="flexWrapContainer">
            <ValueLabel
              label="Total Liabilities"
              value={formatPrice(balanceSheet?.liabilities?.total, 0)}
              sx={{ fontWeight: "bold !important" }}
              isLoading={isLoadingBalanceSheet}
            />
            <ValueLabel
              label="Current Liabilities"
              value={formatPrice(balanceSheet?.liabilities_current?.total, 0)}
              isLoading={isLoadingBalanceSheet}
            />
            <ValueLabel
              label="Long-term Liabilities"
              value={formatPrice(balanceSheet?.liabilities_noncurrent?.total, 0)}
              isLoading={isLoadingBalanceSheet}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6} className="pageBreakBefore">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Equity
          </Typography>

          {balanceSheet?.equity_container?.map((container) => (
            <Box key={container} sx={{ mt: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, mt: "58px", pb: "28px", textTransform: "capitalize" }}>
                {formatName(container)}
              </Typography>

              <Grid container spacing={2}>
                {Boolean(lineItems?.length) &&
                  lineItems
                    ?.filter((v) => v.container === container)
                    .map((v, index) => (
                      <Grid item key={index} xs={6}>
                        <FormControl fullWidth={true}>
                          <StatefulControlledTextField
                            label={formatName(v.item)}
                            name={v.item}
                            isRequired={false}
                            defaultValue={v.value}
                            variant="outlined"
                            onChange={(e) => handleChangeState_LineItem(e, v)}
                            InputLabelProps={{ shrink: true, sx: { textTransform: "capitalize" } }}
                            endAdornment={
                              !BALANCE_SHEET_REQUIRED_FIELDS.includes(v.item) && (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => handleDeleteLineItem(v)} edge="end" color="error">
                                    <DeleteIcon />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                            maskType={MASK_NUMERIC}
                          />
                        </FormControl>

                        <br />
                      </Grid>
                    ))}

                <Grid
                  item
                  xs={6}
                  sx={{ mb: "20px", pt: "44px !important" }}
                  className="navigation"
                  display={"flex"}
                  alignItems={"end"}
                >
                  <Button
                    fullWidth
                    sx={{ borderRadius: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleClickAddNewLineItem(container)}
                  >
                    <AddCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Typography variant="h6">Totals</Typography>
          <Box className="flexWrapContainer">
            <ValueLabel
              label="Owners' Equity"
              value={formatPrice(balanceSheet?.equity?.total, 0)}
              sx={{ fontWeight: "bold !important" }}
              isLoading={isLoadingBalanceSheet}
            />
          </Box>
        </Grid>
      </Grid>

      <WorkspaceActionBar content={<></>} />

      {prompt?.component}
      {confirmDialogComponent}
    </>
  );
}
